import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// @mui
import { Stack, Skeleton, Grid, Typography, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { ClientReqTable } from '../sections/@dashboard/ReservatioRequests';
import Layout from '../components/Layout/Layout';
import SearchComponet from '../components/search/Search';
import CustomTabs from '../components/CustomTabs/ReviewTabs';
import { getReservationRequestssList, getInvoiceDetails, updateReservationRequestStatus, approveReservation, rejectReservation, toggleSaleStatus, getListLookupValuesByCode } from '../network/apis'; // Import the API

export default function ReservationsRequests() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const tabs = [t('reversationRequest.salesTab'), t('reversationRequest.rentTab')];
  const [loading, setLoading] = useState(false);
  const [clientReqList, setClientReqList] = useState([]);

  // Sale Type filter state
  const [saleType, setSaleType] = useState('');
  const [saleTypesList, setSaleTypesList] = useState([]);

  // Status filter state
  const [requestStatus, setRequestStatus] = useState('');

  const rawReservationInvoice = localStorage.getItem('reservationInvoice') || '';
  const reservationInvoice = rawReservationInvoice.trim();

  const clientRequests = (page, search, listingType, currentSaleType, currentRequestStatus) => {
    setLoading(true);
    getReservationRequestssList(page, search, listingType, currentSaleType, currentRequestStatus)
      .then((res) => {
        setClientReqList(res.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching admin list:', error);
        toast.error(error.response?.data?.MESSAGE || 'Error');
        setLoading(false);
      });
  };

  useEffect(() => {
    getListLookupValuesByCode('SALE_TYPE')
      .then((res) => {
        setSaleTypesList(res?.data?.content || res?.data || []);
      })
      .catch((err) => console.error('Failed to fetch sale types lookup:', err));
  }, []);

  useEffect(() => {
    if (reservationInvoice) {
      setSearch(reservationInvoice);
    }
    const listingType = tabValue === 0 ? 'SALE' : 'RENT';
    clientRequests(page, search, listingType, saleType, requestStatus);
  }, [page, search, reservationInvoice, tabValue, saleType, requestStatus]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
    setSaleType(''); // reset sale type when switching tabs
    setRequestStatus(''); // reset status filter
  };

  const handleSaleTypeChange = (event, newSaleType) => {
    if (newSaleType !== null) {
      setSaleType(newSaleType);
      setPage(0);
    }
  };

  const handleStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setRequestStatus(newStatus);
      setPage(0);
    }
  };

  const handleApprove = (id) => {
    approveReservation({ id })
      .then((res) => {
        if (res.data.STATUS === 'FAILED' || res.data.STATUS === '0') {
          toast.error(res.data.MESSAGE);
        } else {
          toast.success(res.data.MESSAGE || 'Approved successfully');
        }
        clientRequests(page, search, tabValue === 0 ? 'SALE' : 'RENT', saleType, requestStatus);
      })
      .catch((error) => {
        toast.error(error.response?.data?.MESSAGE || 'Error approving reservation');
        clientRequests(page, search, tabValue === 0 ? 'SALE' : 'RENT', saleType, requestStatus);
      });
  };

  const handleReject = (id) => {
    rejectReservation({ id })
      .then((res) => {
        if (res.data.STATUS === 'FAILED' || res.data.STATUS === '0') {
          toast.error(res.data.MESSAGE);
        } else {
          toast.success(res.data.MESSAGE || 'Rejected successfully');
        }
        clientRequests(page, search, tabValue === 0 ? 'SALE' : 'RENT', saleType, requestStatus);
      })
      .catch((error) => {
        toast.error(error.response?.data?.MESSAGE || 'Error rejecting reservation');
        clientRequests(page, search, tabValue === 0 ? 'SALE' : 'RENT', saleType, requestStatus);
      });
  };

  const onUpdateSoldStatus = ({ id, status }) => {
    toggleSaleStatus({ id, status })
      .then((res) => {
        if (res.data.STATUS === 'FAILED' || res.data.STATUS === '0') {
          toast.error(res.data.MESSAGE);
        } else {
          toast.success(res.data.MESSAGE || 'Status updated successfully');
        }
        clientRequests(page, search, tabValue === 0 ? 'SALE' : 'RENT', saleType, requestStatus);
      })
      .catch((error) => {
        toast.error(error.response?.data?.MESSAGE || 'Error updating status');
        clientRequests(page, search, tabValue === 0 ? 'SALE' : 'RENT', saleType, requestStatus);
      });
  };

  const handleDownload = (id) => {
    getInvoiceDetails(id)
      .then((res) => {
        const contentDisposition = res.headers['content-disposition'];
        let fileName = 'invoice.pdf';

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
        }

        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      })
      .catch((error) => {
        console.error('Error downloading the file:', error);
        toast.error(error.response?.data?.MESSAGE || 'Error downloading file');
      });
  };

  return (
    <>
      <Helmet>
        <title> {t('reversationRequest.reservationsTitle')}</title>
      </Helmet>

      <Layout>
        <Grid marginBottom={3} display="flex" alignItems={'center'} justifyContent="space-between" md={6} xs={12}>
          <Typography sx={{ fontSize: '24px', fontWeight: '700', lineHeight: '32px', color: '#111827' }}>
            {t('reversationRequest.reservationsTitle')}
          </Typography>
        </Grid>

        {/* 1. Tabs taking first position */}
        <Grid mb={3} display="flex">
          <CustomTabs value={tabValue} onChange={handleTabChange} tabs={tabs} />
        </Grid>

        {/* 2. Filters moved to the right (flex-start in RTL, flex-end in LTR) */}
        <Grid mb={3}
          display="flex"
          justifyContent="flex-start"
          flexWrap="wrap"
          gap={3}
          sx={{
            direction: isRtl ? 'rtl' : 'ltr',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, flexDirection: 'column' }}>
            {/* Sale Type Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#344054', minWidth: '70px' }}>
                {t('reversationRequest.saleType')}:
              </Typography>
              <ToggleButtonGroup
                value={saleType}
                exclusive
                onChange={handleSaleTypeChange}
                aria-label="Sale Type"
                size="small"
                sx={{
                  direction: isRtl ? 'rtl' : 'ltr',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid #E4E7EC !important',
                    borderRadius: '8px !important',
                    px: 2,
                    py: 0.5,
                    mx: 0.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#475467',
                    '&.Mui-selected': {
                      backgroundColor: '#E0FFE1 !important',
                      color: '#30A14E !important',
                      borderColor: '#30A14E !important',
                    },
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    }
                  },
                  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                    marginLeft: 0.5,
                    borderLeft: '1px solid #E4E7EC !important',
                  }
                }}
              >
                <ToggleButton value="">
                  {isRtl ? 'الكل' : 'All'}
                </ToggleButton>
                {saleTypesList.map((item) => (
                  <ToggleButton key={item.id} value={item.value}>
                    {isRtl ? item.labelAr : item.labelEn}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Status Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#344054', minWidth: '70px' }}>
                {t('reversationRequest.filterStatus')}:
              </Typography>
              <ToggleButtonGroup
                value={requestStatus}
                exclusive
                onChange={handleStatusChange}
                aria-label="Request Status"
                size="small"
                sx={{
                  direction: isRtl ? 'rtl' : 'ltr',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid #E4E7EC !important',
                    borderRadius: '8px !important',
                    px: 2,
                    py: 0.5,
                    mx: 0.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#475467',
                    '&.Mui-selected': {
                      backgroundColor: '#E0FFE1 !important',
                      color: '#30A14E !important',
                      borderColor: '#30A14E !important',
                    },
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    }
                  },
                  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                    marginLeft: 0.5,
                    borderLeft: '1px solid #E4E7EC !important',
                  }
                }}
              >
                <ToggleButton value="">
                  {isRtl ? 'الكل' : 'All'}
                </ToggleButton>
                <ToggleButton value="PENDING">
                  {t('reversationRequest.PENDING')}
                </ToggleButton>
                <ToggleButton value="COMPLETED">
                  {t('reversationRequest.contacted')}
                </ToggleButton>
                <ToggleButton value="CANCELLED">
                  {t('reversationRequest.CANCELED')}
                </ToggleButton>
                <ToggleButton value="EXPIRED">
                  {t('reversationRequest.EXPIRED')}
                </ToggleButton>
                <ToggleButton value="FULFILLED">
                  {tabValue === 1
                    ? t('reversationRequest.FULFILLED_RENT')
                    : t('reversationRequest.FULFILLED_SALE')
                  }
                </ToggleButton>
                <ToggleButton value="TRANSACTION_FAILED">
                  {t('reversationRequest.TRANSACTION_FAILED')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Grid>

        {/* 3. Search component taking full column */}
        <Grid mb={3} container>
          <Grid item xs={12}>
            <SearchComponet onSearch={setSearch} style={{ width: '100%' }} label={t('reversationRequest.searchByUnitName')} />
          </Grid>
        </Grid>

        {loading ? (
          <Stack>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </Stack>
        ) : (
          <>
            <ClientReqTable
              tabValue={tabValue}
              handleChangePage={handleChangePage}
              page={page}
              requests={clientReqList}
              onDownload={handleDownload}
              onApprove={handleApprove}
              onReject={handleReject}
              onUpdateSoldStatus={onUpdateSoldStatus}
            />
          </>
        )}
      </Layout>
    </>
  );
}
